import React, { useEffect, useState } from "react";
import axios from "axios";
import CustomButton from "../pages/CustomButton";
import { getAuthHeaders } from "../services/useAuth";
import { BASE_URL } from "../config";
import { formatDate } from "../utils/formatDate";
import Loader from "../dist/Loader";

const TravelPolicy = () => {
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTable, setShowTable] = useState(false);

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setSelectedDate(today);

    axios
      .get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() })
      .then((res) => setStates(res.data))
      .catch((err) => console.error("Error fetching states:", err));

    axios
      .get(`${BASE_URL}/api/divisions`, { headers: getAuthHeaders() })
      .then((res) => setDivisions(res.data))
      .catch((err) => console.error("Error fetching divisions:", err));
  }, []);

  const fetchPolicies = async (filters = {}) => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/employees-travel-policy/`, {
        headers: getAuthHeaders(),
        params: filters,
      });

      if (Array.isArray(res.data.employees)) {
        setPolicies(res.data.employees);
      } else {
        setPolicies([]);
        alert("No data found");
      }
    } catch (err) {
      console.error("Error fetching policies:", err);
      alert("Failed to fetch travel policies");
    } finally {
      setLoading(false);
    }
  };

  const handleGoClick = () => {
    if (!selectedState && !selectedDivision) {
      alert("Please select at least State or Division.");
      return;
    }

    const filters = {
      date: selectedDate, // already auto-selected
    };

    if (selectedState) filters.state_id = selectedState;
    if (selectedDivision) filters.division_id = selectedDivision;

    fetchPolicies(filters);
    setShowTable(true);
  };

  return (
    <div className="p-2  border-2 border-gray-300 rounded-lg bg-white">
      <h1 className="text-2xl font-bold text-center mb-6 text-blue-600">
        Travel Policy
      </h1>

      {/* Filters */}
      <div className="bg-white shadow-md rounded-xl p-4 mb-6 flex flex-col items-center">
        <div className="flex flex-wrap gap-4 justify-center w-full">
          <div>
            <label className="block mb-1 font-semibold">State</label>
            <select
              className="w-full border rounded px-2 py-2"
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
            >
              <option value="">Select State</option>
              {states.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block mb-1 font-semibold">Division</label>
            <select
              className="w-full border rounded px-2 py-2"
              value={selectedDivision}
              onChange={(e) => setSelectedDivision(e.target.value)}
            >
              <option value="">Select Division</option>
              {divisions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block mb-1 font-semibold">Date</label>
            <input
              type="date"
              className="w-full border rounded px-2 py-2"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
        </div>

        <div className="flex justify-center mt-4">
          <CustomButton
            className="bg-green-500 text-white font-medium px-6 py-2 rounded"
            onClick={handleGoClick}
          >
            GO
          </CustomButton>
        </div>
      </div>

      {/* Show selected date */}
      {showTable && (
        <div className="mb-4 text-center font-semibold text-gray-700">
          Showing Travel Policy for: {formatDate(selectedDate)}
        </div>
      )}

      {/* Table Section */}
      {showTable && (
        <div className="border rounded shadow table-wrapper">
          {/* Loader */}
          {loading && (
            <div className="text-center py-4">
              <Loader onCancel={() => setLoading(false)} />
            </div>
          )}

          {/* Table (only when not loading) */}
          {!loading && (
            <table className="unlock-dcr-leave-table text-sm">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="border p-2 sticky top-0 bg-blue-500 z-40">
                    S.No
                  </th>
                  <th className="border p-2 sticky top-0 bg-blue-500 z-40">
                    Employee ID
                  </th>
                  <th className="border p-2 sticky top-0 bg-blue-500 z-40">
                    State
                  </th>
                  <th className="border p-2 sticky top-0 bg-blue-500 z-40">
                    Division
                  </th>
                  <th className="border p-2 sticky top-0 bg-blue-500 z-40">
                    Employee
                  </th>
                  <th className="border p-2 sticky top-0 bg-blue-500 z-40">
                    Designation
                  </th>
                  <th className="border p-2 sticky top-0 bg-blue-500 z-40">
                    HQ
                  </th>
                  <th className="border p-2 sticky top-0 bg-blue-500 z-40">
                    DOJ
                  </th>
                  <th className="border p-2 sticky top-0 bg-blue-500 z-40">
                    Phone
                  </th>
                  <th className="border p-2 sticky top-0 bg-blue-500 z-40">
                    Manager
                  </th>
                  <th className="border p-2 sticky top-0 bg-blue-500 z-40">
                    Rate/KM
                  </th>
                  <th className="border p-2 sticky top-0 bg-blue-500 z-40">
                    DA HQ
                  </th>
                  <th className="border p-2 sticky top-0 bg-blue-500 z-40">
                    DA Ex
                  </th>
                  <th className="border p-2 sticky top-0 bg-blue-500 z-40">
                    DA Out
                  </th>
                  <th className="border p-2 sticky top-0 bg-blue-500 z-40">
                    Hotel
                  </th>
                  <th className="border p-2 sticky top-0 bg-blue-500 z-40">
                    Other
                  </th>
                  <th className="border p-2 sticky top-0 bg-blue-500 z-40">
                    VMA
                  </th>
                  <th className="border p-2 sticky top-0 bg-blue-500 z-40">
                    Fooding
                  </th>
                  <th className="border p-2 sticky top-0 bg-blue-500 z-40">
                    INT Charge
                  </th>
                  <th className="border p-2 sticky top-0 bg-blue-500 z-40">
                    Phone Allowance
                  </th>
                  <th className="border p-2 sticky top-0 bg-blue-500 z-40">
                    Total
                  </th>
                  <th className="border p-2 sticky top-0 bg-blue-500 z-40">
                    Remarks
                  </th>
                  <th className="border p-2 sticky top-0 bg-blue-500 z-40">
                    Last Update
                  </th>
                </tr>
              </thead>

              <tbody>
                {policies.length === 0 ? (
                  <tr>
                    <td colSpan="23" className="text-center p-4">
                      No data found
                    </td>
                  </tr>
                ) : (
                  policies.map((row, index) => (
                    <tr key={row.employee_id}>
                      <td className="border p-2">{index + 1}</td>
                      <td className="border p-2">{row.employee_id}</td>
                      <td className="border p-2">{row.state}</td>
                      <td className="border p-2">{row.division}</td>
                      <td className="border p-2">{row.employee}</td>
                      <td className="border p-2">{row.designation}</td>
                      <td className="border p-2">{row.hq}</td>
                      <td className="border p-2">{formatDate(row.doj)}</td>
                      <td className="border p-2">{row.phone}</td>
                      <td className="border p-2">{row.manager || "-"}</td>
                      <td className="border p-2">{row.rate_per_km}</td>
                      <td className="border p-2">{row.da_hq}</td>
                      <td className="border p-2">{row.da_ex}</td>
                      <td className="border p-2">{row.da_out}</td>
                      <td className="border p-2">{row.hotel}</td>
                      <td className="border p-2">{row.other}</td>
                      <td className="border p-2">{row.vma}</td>
                      <td className="border p-2">{row.fooding}</td>
                      <td className="border p-2">{row.int_charge}</td>
                      <td className="border p-2">{row.phone_allowance}</td>
                      <td className="border p-2">{row.total}</td>
                      <td className="border p-2">{row.remarks}</td>
                      <td className="border p-2">{row.last_update}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default TravelPolicy;
